<template>
    <p-card class="form-card">
        <template #title>
            {{ formTitle }}
        </template>
        <template #content>
            <form-field
                v-slot="{ onBlur }"
                label-key="labels.user.username"
                :validation-state="validationState.result.value.username"
                required
            >
                <p-input-text
                    v-model="invite.username"
                    class="lg:w-5 w-full"
                    @blur="onBlur"
                />
            </form-field>
            <form-field
                v-slot="{ onBlur }"
                label-key="labels.user.password"
                :validation-state="validationState.result.value.password"
                required
            >
                <p-input-text
                    v-model="invite.password"
                    type="password"
                    class="lg:w-5 w-full"
                    @blur="onBlur"
                />
            </form-field>
            <form-field
                v-slot="{ onBlur }"
                label-key="labels.user.confirmPassword"
                :validation-state="validationState.result.value.confirmPassword"
                required
            >
                <p-input-text
                    v-model="confirmPassword"
                    type="password"
                    class="lg:w-5 w-full"
                    @blur="onBlur"
                />
            </form-field>
            <div class="mt-10">
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion1"
                    :validation-state="validationState.result.value.securityQuestion1Id"
                    required
                >
                    <p-dropdown
                        v-model="invite.securityQuestion1Id"
                        :options="securityQuestions1"
                        option-label="securityQuestion"
                        option-value="id"
                        class="w-full"
                        @input="onBlur"
                    />
                </form-field>
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion1Answer"
                    :validation-state="validationState.result.value.securityQuestion1Answer"
                    required
                >
                    <p-input-text
                        v-model="invite.securityQuestion1Answer"
                        class="lg:w-5 w-full"
                        @blur="onBlur"
                    />
                </form-field>
            </div>
            <div class="mt-10">
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion2"
                    :validation-state="validationState.result.value.securityQuestion2Id"
                    required
                >
                    <p-dropdown
                        v-model="invite.securityQuestion2Id"
                        :options="securityQuestions2"
                        option-label="securityQuestion"
                        option-value="id"
                        class="w-full"
                        @input="onBlur"
                    />
                </form-field>
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion2Answer"
                    :validation-state="validationState.result.value.securityQuestion2Answer"
                    required
                >
                    <p-input-text
                        v-model="invite.securityQuestion2Answer"
                        class="lg:w-5 w-full"
                        @blur="onBlur"
                    />
                </form-field>
            </div>
            <div class="mt-10">
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion3"
                    :validation-state="validationState.result.value.securityQuestion3Id"
                    required
                >
                    <p-dropdown
                        v-model="invite.securityQuestion3Id"
                        :options="securityQuestions3"
                        option-label="securityQuestion"
                        option-value="id"
                        class="w-full"
                        @input="onBlur"
                    />
                </form-field>
                <form-field
                    v-slot="{ onBlur }"
                    label-key="labels.user.securityQuestion3Answer"
                    :validation-state="validationState.result.value.securityQuestion3Answer"
                    required
                >
                    <p-input-text
                        v-model="invite.securityQuestion3Answer"
                        class="lg:w-5 w-full"
                        @blur="onBlur"
                    />
                </form-field>
            </div>
            <p-message v-if="validationError" severity="error">
                {{ validationError }}
            </p-message>
        </template>
        <template #footer>
            <p-button :disabled="savePending" @click="register">
                Register
            </p-button>
        </template>
    </p-card>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { z } from "zod";
import axios from "axios";
import { useRootStore } from "@/store";
import { Alert } from "@/store/classes";
import { ContactUserAuthViewModel, ContactUserSecurityQuestion } from "@/store/classes";
import { useI18n } from "vue-i18n";
import FormField from "@/components/common/FormField.vue3.vue"
import { useFormValidation } from "@/components/common/FormValidation";
import { DropDownValueSchema, PasswordSchema, RequiredStringSchema } from "@/components/common/schemas";

const props = defineProps<{
    inviteKey: string;
    securityQuestions: ContactUserSecurityQuestion[];
    validationCode?: string;
    formTitle: string;
}>();
const emit = defineEmits(['registered']);

const rootStore = useRootStore();
const { t } = useI18n();

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);

const inviteRegisterSchema = z.object({
    username: RequiredStringSchema,
    password: RequiredStringSchema.and(PasswordSchema),
    confirmPassword: RequiredStringSchema,
    securityQuestion1Id: DropDownValueSchema,
    securityQuestion1Answer: RequiredStringSchema,
    securityQuestion2Id: DropDownValueSchema,
    securityQuestion2Answer: RequiredStringSchema,
    securityQuestion3Id: DropDownValueSchema,
    securityQuestion3Answer: RequiredStringSchema,
}).superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            message: t('message.validation.confirmPasswordMatch'),
            code: 'custom',
            path: ['confirmPassword']
        });
    }
});

const savePending = ref(false);
const validationError = ref('');
const invite = ref({
    username: '',
    password: '',
    securityQuestion1Id: undefined,
    securityQuestion1Answer: '',
    securityQuestion2Id: undefined,
    securityQuestion2Answer: '',
    securityQuestion3Id: undefined,
    securityQuestion3Answer: ''
} as ContactUserAuthViewModel);
const confirmPassword = ref('');

const validationState = useFormValidation<z.infer<typeof inviteRegisterSchema>>(
    inviteRegisterSchema,
    computed(() => ({
        ...invite.value,
        confirmPassword: confirmPassword.value,
    })),
    false
);

const securityQuestions1 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion2Id && question.id !== invite.value.securityQuestion3Id);
});

const securityQuestions2 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion1Id && question.id !== invite.value.securityQuestion3Id);
});

const securityQuestions3 = computed(() => {
    return props.securityQuestions.filter(question => question.id !== invite.value.securityQuestion1Id && question.id !== invite.value.securityQuestion2Id);
});

async function register(): Promise<void> {
    if (savePending.value) return;
    savePending.value = true;
    if (!validationState.validate()) {
        savePending.value = false;
        return;
    }
    try {
        await showLoader('invite');
        validationError.value = '';
        await axios.post(`/auth/invite/${props.inviteKey}/validate`, invite.value);
        await axios.put(`/auth/invite/${props.inviteKey}`, {
            ...invite.value,
            intakeValidationCode: props.validationCode
        });
    } catch (e: any) {
        if (e.response?.status === 400 && e.response?.data)
            validationError.value = e.response.data;
        else
            await showAlert({ text: 'Unable to register, please try again', type: 'error' });
        return;
    } finally {
        await hideLoader('invite');
        savePending.value = false;
    }
    emit('registered');
}
</script>
<style scoped lang="scss">
:deep(.p-dropdown-label) {
    white-space: pre-line;
}

:deep(.p-dropdown-panel) {
    width: 100%;
}

:deep(.p-dropdown-items) {
    width: fit-content;
    min-width: 100%;
}
</style>
